import { useCallback, useEffect, useState } from 'react';
import { funnelApi, ENQUIRY_CHANNELS, OUTCOMES, type FunnelBoardView, type EnquiryView } from '../lib/funnelApi';
import { formatPaise } from '../lib/money';

/**
 * ═══ DID THE MONEY TURN INTO STUDENTS? ═══
 *
 * ⚠ THE SOURCE TABLE IS THE PRODUCT. Every college already knows how many enquiries it got; none of
 * them knows which of the things they paid for produced a student. Stage counts are on this screen
 * because a principal expects to see them, but they are not why anybody buys it.
 *
 * ⚠ NO MONEY ON THE COUNSELLOR'S VIEW. Spend and cost-per-admission are admin-only on the server
 * and simply absent here for anybody else — the same rule that keeps fee figures off a teacher's
 * screen everywhere else in this product.
 */

const TONE = {
  good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b',
};

/**
 * ⚠ A TABLE IS THE WRONG SHAPE ON A PHONE, and this one proved it. Six columns clipped the
 * conversion percentage — the single number the table exists to show — off the right-hand edge,
 * behind a horizontal scroll nobody discovers. A counsellor stands in a corridor with a phone; she
 * is the person most likely to read this and the least able to.
 */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.('(max-width: 620px)');
    setNarrow(!!m?.matches);
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    m?.addEventListener?.('change', on);
    return () => m?.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

/**
 * The stored value, as a person reads it.
 *
 * ⚠ THE HISTORY IS THE PART SOMEBODY SCANS, so it cannot say "NoAnswer". The label comes from the
 * same table the picker offers, which means a wording change happens in one place — and an outcome
 * that a channel does not offer still renders rather than vanishing, because old rows exist.
 */
function outcomeLabel(channel: string, outcome: string): string {
  const found = (OUTCOMES[channel] ?? []).find((o) => o.value === outcome);
  if (found) return found.label.toLowerCase();
  for (const list of Object.values(OUTCOMES)) {
    const any = list.find((o) => o.value === outcome);
    if (any) return any.label.toLowerCase();
  }
  return outcome.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

export function AdmissionsFunnelPage() {
  const [b, setB] = useState<FunnelBoardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const narrow = useIsNarrow();

  const load = useCallback(async () => {
    try { setB(await funnelApi.board()); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load the funnel.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!b) return <div className="card" style={{ padding: 24, color: 'var(--ink-3)' }}>{error ?? 'Loading…'}</div>;

  const anySpend = b.sources.some((s) => s.spentPaise !== null);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Enquiries</h1>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setCapturing(true)}>
          + Log an enquiry
        </button>
      </div>

      <div className="card" style={{ padding: '22px 24px', marginBottom: 14 }}>
        {/* ⚠ The most urgent TRUE thing, not an average of everything. */}
        <p style={{ fontSize: 15, margin: 0, maxWidth: 640, lineHeight: 1.5 }}>{b.headline}</p>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 10, marginBottom: 14,
      }}>
        <Tile label="Open enquiries" value={b.openEnquiries} sub={`${b.stale} not touched in a fortnight`}
              tone={b.stale > 0 ? 'warning' : undefined} />
        <Tile label="Admitted" value={b.admittedThisSeason}
              sub={b.medianDaysToAdmit === null ? 'none yet' : `${b.medianDaysToAdmit} days, typically`} />
        <Tile label="Waiting on you" value={b.needChasingToday}
              sub={b.neverContacted > 0 ? `${b.neverContacted} never contacted` : 'all have been contacted'}
              tone={b.neverContacted > 0 ? 'critical' : b.needChasingToday > 0 ? 'warning' : undefined} />
        <Tile label="Convert" value={`${b.overallConversionPercent}%`}
              sub={`of enquiries older than ${b.settlingDays} days`} />
      </div>

      {/* ⚠ SAID OUT LOUD, NOT HIDDEN. A rate computed on a subset that the reader does not know
          about is a rate the reader will misread — and then distrust when it moves. */}
      {b.unsettledEnquiries > 0 && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 14px' }}>
          {b.unsettledEnquiries} {b.unsettledEnquiries === 1 ? 'enquiry is' : 'enquiries are'} newer than{' '}
          {b.settlingDays} days and left out of every rate below — they have not failed to convert, they
          have not had time to.
        </p>
      )}

      <div className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', fontWeight: 650, fontSize: 13.5 }}>
          Where the admissions came from
        </div>
        {b.sources.length === 0 ? (
          <p style={{ padding: '0 16px 16px', fontSize: 13, color: 'var(--ink-3)', margin: 0 }}>
            Nothing settled yet. As soon as enquiries are older than {b.settlingDays} days they appear here.
          </p>
        ) : narrow ? (
          /* ⚠ STACKED, NOT SCROLLED. Every figure is on screen; nothing hides behind an edge. */
          b.sources.map((s) => (
            <div key={s.key} style={{ padding: '10px 16px', borderTop: '1px solid var(--line-2)' }}>
              <div style={{ fontSize: 13, fontWeight: 550 }}>
                {s.channelLabel}
                {s.campaign && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> · {s.campaign}</span>}
                {s.isReferral && (
                  <span className="eyebrow" style={{ fontSize: 9, marginLeft: 6, color: TONE.good }}>free</span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
                <strong>{s.conversionPercent}%</strong> convert · {s.admitted} of {s.enquiries}
                {anySpend && s.spentPaise !== null && (
                  <> · {formatPaise(s.spentPaise)} spent
                    {s.costPerAdmissionPaise !== null
                      ? <> · {formatPaise(s.costPerAdmissionPaise)} each</>
                      : <span style={{ color: TONE.critical }}> · nothing yet</span>}
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <Th>Source</Th><Th right>Enquiries</Th><Th right>Admitted</Th><Th right>Convert</Th>
                {anySpend && <><Th right>Spent</Th><Th right>Per admission</Th></>}
              </tr>
            </thead>
            <tbody>
              {b.sources.map((s) => (
                <tr key={s.key} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td>
                    <span style={{ fontWeight: 550 }}>{s.channelLabel}</span>
                    {s.campaign && <span style={{ color: 'var(--ink-3)' }}> · {s.campaign}</span>}
                    {/* ⚠ Referrals flagged because they cost nothing, convert best, and are the
                        only source a school can deliberately produce more of. */}
                    {s.isReferral && (
                      <span className="eyebrow" style={{ fontSize: 9, marginLeft: 6, color: TONE.good }}>free</span>
                    )}
                  </Td>
                  <Td right>{s.enquiries}</Td>
                  <Td right>{s.admitted}</Td>
                  <Td right><strong>{s.conversionPercent}%</strong></Td>
                  {anySpend && (
                    <>
                      {/* ⚠ An em dash, never ₹0. Nobody costed this source; a zero would be a lie
                          the table tells confidently. */}
                      <Td right>{s.spentPaise === null ? '—' : formatPaise(s.spentPaise)}</Td>
                      <Td right>
                        {s.costPerAdmissionPaise !== null
                          ? formatPaise(s.costPerAdmissionPaise)
                          : s.spentPaise
                            ? <span style={{ color: TONE.critical }}>nothing yet</span>
                            : '—'}
                      </Td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', fontWeight: 650, fontSize: 13.5 }}>
          Waiting on you{b.chases.length > 0 && <span style={{ color: 'var(--ink-3)' }}> · {b.chases.length}</span>}
        </div>
        {b.chases.length === 0 ? (
          <p style={{ padding: '0 16px 16px', fontSize: 13, color: 'var(--ink-3)', margin: 0 }}>
            Nobody is waiting. Every open enquiry has somebody on it and a next step.
          </p>
        ) : b.chases.map((c) => (
          <button key={c.id} onClick={() => setOpen(c.id)} style={{
            display: 'flex', width: '100%', gap: 10, alignItems: 'center', textAlign: 'left',
            padding: '10px 16px', background: 'none', border: 0, borderTop: '1px solid var(--line-2)',
            cursor: 'pointer',
          }}>
            <span style={{ fontSize: 13, fontWeight: 550 }}>{c.applicantName || 'Name not taken yet'}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.why}</span>
            {c.daysOverdue > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: TONE.critical }}>
                {c.daysOverdue} {c.daysOverdue === 1 ? 'day' : 'days'} late
              </span>
            )}
          </button>
        ))}
      </div>

      {open && <EnquiryPanel id={open} onClose={() => { setOpen(null); void load(); }} />}
      {capturing && <CapturePanel onClose={(saved) => { setCapturing(false); if (saved) void load(); }} />}
    </div>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: number | string; sub: string; tone?: keyof typeof TONE }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 650, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, marginTop: 2, display: 'flex', gap: 5, alignItems: 'center' }}>
        {/* ⚠ A dot beside words, never coloured text alone. */}
        {tone && <span aria-hidden="true" style={{
          width: 7, height: 7, borderRadius: 999, background: TONE[tone], flex: '0 0 auto',
        }} />}
        <span style={{ color: 'var(--ink-3)' }}>{sub}</span>
      </div>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <th style={{
    padding: '8px 16px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)',
    textAlign: right ? 'right' : 'left', borderTop: '1px solid var(--line-2)',
  }}>{children}</th>;
}

function Td({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <td style={{
    padding: '9px 16px', textAlign: right ? 'right' : 'left',
    fontVariantNumeric: right ? 'tabular-nums' : undefined,
  }}>{children}</td>;
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto',
    }}>
      <div className="card" onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 560, width: '100%', padding: 20, marginTop: 40 }}>
        {children}
      </div>
    </div>
  );
}

/** One enquiry: who they are, how they were chased, and what happens next. */
function EnquiryPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const [e, setE] = useState<EnquiryView | null>(null);
  const [channel, setChannel] = useState('Call');
  const [outcome, setOutcome] = useState('Spoke');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setE(await funnelApi.get(id)); }
    catch (x) { setErr(x instanceof Error ? x.message : 'Could not load it.'); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const log = async (direction: 'Outbound' | 'Inbound') => {
    setBusy(true); setErr(null);
    try {
      setE(await funnelApi.logContact(id, { channel, direction, outcome, note: note || null }));
      setNote('');
    } catch (x) { setErr(x instanceof Error ? x.message : 'Could not record it.'); }
    finally { setBusy(false); }
  };

  if (!e) return <Overlay onClose={onClose}><p style={{ color: 'var(--ink-3)' }}>{err ?? 'Loading…'}</p></Overlay>;

  const outcomes = OUTCOMES[channel] ?? OUTCOMES.Call;

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <h2 style={{ fontSize: 17, margin: 0 }}>{e.applicantName || 'Name not taken yet'}</h2>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{e.reference}</span>
        <button className="btn" style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' }}
                onClick={onClose}>Close</button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 10 }}>
        {e.channelLabel}{e.campaign && ` · ${e.campaign}`}
        {e.guardianMobile && ` · ${e.guardianMobile}`}
      </div>

      {/* ⚠ THE HONEST SENTENCE, not a bare count. "8 attempts" reads as chased; "8 attempts,
          nobody has spoken to them yet" reads as what it is. */}
      <p style={{ fontSize: 13, margin: '0 0 12px' }}>{e.contact.words}</p>

      {/* ⚠ Names the missing fields rather than greying out a button with no explanation. */}
      {e.missingForApplication.length > 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 12px' }}>
          Before this can become an application, somebody has to collect{' '}
          {e.missingForApplication.join(', ')}.
        </p>
      )}

      {e.contact.hasOptedOut ? (
        // ⚠ NO LOGGING FORM AT ALL for somebody who asked to be left alone. Greying out the button
        //   would still invite the workaround; removing it is the honest design.
        <p style={{ fontSize: 13, color: TONE.critical, margin: '0 0 12px' }}>
          They have asked not to be contacted. Nothing further should be sent or dialled.
        </p>
      ) : (
        <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 6 }}>Record a contact</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            <select className="input" style={{ width: 'auto' }} value={channel}
                    onChange={(x) => {
                      setChannel(x.target.value);
                      // ⚠ Reset the outcome — "Read" beside a phone call is how a list gets filled
                      //   in wrongly and then reported on confidently.
                      setOutcome((OUTCOMES[x.target.value] ?? OUTCOMES.Call)[0].value);
                    }}>
              {Object.keys(OUTCOMES).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="input" style={{ width: 'auto' }} value={outcome}
                    onChange={(x) => setOutcome(x.target.value)}>
              {outcomes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <input className="input" placeholder="What was said?" value={note}
                 style={{ marginBottom: 6 }} onChange={(x) => setNote(x.target.value)} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" disabled={busy} onClick={() => void log('Outbound')}>We contacted them</button>
            {/* ⚠ Inbound is worth more than an outbound attempt and is recorded separately. */}
            <button className="btn" disabled={busy} onClick={() => void log('Inbound')}>They contacted us</button>
          </div>
        </div>
      )}

      {err && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{err}</p>}

      {e.attempts.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 10 }}>
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 5 }}>Every contact, newest first</div>
          {e.attempts.map((a, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 3 }}>
              <span style={{ color: 'var(--ink-3)' }}>
                {new Date(a.at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>{' '}
              {a.direction === 'Inbound' ? '←' : '→'} {a.channel} · {outcomeLabel(a.channel, a.outcome)}
              {a.note && <span style={{ color: 'var(--ink-3)' }}> · {a.note}</span>}
            </div>
          ))}
        </div>
      )}
    </Overlay>
  );
}

/** Log an enquiry in two seconds. ⚠ A number and where they came from is the whole required form. */
function CapturePanel({ onClose }: { onClose: (saved: boolean) => void }) {
  const [mobile, setMobile] = useState('');
  const [channel, setChannel] = useState('Phone');
  const [campaign, setCampaign] = useState('');
  const [name, setName] = useState('');
  const [seeking, setSeeking] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dupes, setDupes] = useState<{ id: string; applicantName: string; guardianMobile: string }[]>([]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await funnelApi.capture({
        institutionId: undefined, guardianMobile: mobile, guardianEmail: null,
        channel, campaign: campaign || null, channelDetail: null,
        applicantName: name || null, seekingClass: seeking || null, guardianName: null,
        ownerUserId: null, notes: null,
      });
      // ⚠ Duplicates are SHOWN, not merged automatically. A website form on Monday and a phone call
      //   on Wednesday from the same mother is the commonest sequence there is, and only a person
      //   can tell that from two children in one family.
      if (r.possibleDuplicates?.length) { setDupes(r.possibleDuplicates); return; }
      onClose(true);
    } catch (x) { setErr(x instanceof Error ? x.message : 'Could not save it.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={() => onClose(dupes.length > 0)}>
      <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Log an enquiry</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 12px' }}>
        A number and where they came from is enough. The rest can be collected on the call.
      </p>

      <input className="input" placeholder="Mobile number" value={mobile}
             style={{ marginBottom: 8 }} onChange={(e) => setMobile(e.target.value)} />
      <select className="input" value={channel} style={{ marginBottom: 8 }}
              onChange={(e) => setChannel(e.target.value)}>
        {ENQUIRY_CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <input className="input" placeholder="Campaign (optional) — e.g. Admission Jan 2027"
             value={campaign} style={{ marginBottom: 8 }} onChange={(e) => setCampaign(e.target.value)} />
      <input className="input" placeholder="Student's name (optional)" value={name}
             style={{ marginBottom: 8 }} onChange={(e) => setName(e.target.value)} />
      <input className="input" placeholder="Class or course (optional)" value={seeking}
             style={{ marginBottom: 12 }} onChange={(e) => setSeeking(e.target.value)} />

      {err && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{err}</p>}

      {dupes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 12.5, color: 'var(--warn)', margin: '0 0 6px' }}>
            Saved — but this number already has {dupes.length === 1 ? 'an open enquiry' : 'open enquiries'}.
            Check before chasing them twice.
          </p>
          {dupes.map((d) => (
            <div key={d.id} style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
              {d.applicantName || 'Name not taken'} · {d.guardianMobile}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={() => onClose(dupes.length > 0)}>
          {dupes.length > 0 ? 'Done' : 'Cancel'}
        </button>
        {dupes.length === 0 && (
          <button className="btn btn-accent" disabled={busy || !mobile.trim()} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Log it'}
          </button>
        )}
      </div>
    </Overlay>
  );
}
